"use client";
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { ApiError } from "@/lib/api";
import type { CommandOptions } from "@/lib/api/suppliers";
import { getOperationByKey } from "@/lib/api/suppliers";
import { supplierOperationQuery, type EventBinding } from "@/lib/api/events";
import { createCommandKey } from "@/lib/commands/command-key";
import {
  eventAuthorityLost,
  eventFeedback,
} from "@/lib/commands/event-feedback";
import {
  eventBindingKey,
  eventCompletion,
  eventIntent,
  type EventOperation,
  type EventOperationType,
  type EventScope,
} from "@/lib/commands/event-operation-store";
import { useEventRecovery } from "./use-event-recovery";
import { invalidateEvent } from "@/lib/commands/event-invalidation";

export interface EventRuntimeConfig {
  binding: EventBinding;
  csrfToken: string;
  verify: (signal: AbortSignal) => Promise<boolean>;
  invalidate: (error: unknown) => void;
}
export type EventFeedback =
  ReturnType<typeof eventFeedback> | "storage" | "blocked" | "success";
type Send = (options: CommandOptions) => Promise<{ data: unknown }>;
interface Runtime {
  config: EventRuntimeConfig;
  binding: EventBinding;
  records: EventOperation[];
  storageAvailable: boolean;
  storageReady: boolean;
  terminals: (EventOperation & { terminalState: "Completed" | "Failed" })[];
  dismiss: (key: string) => void;
  pending: boolean;
  lookupFailed: boolean;
  isDenied: (eventId: string) => boolean;
  accessEpoch: (eventId: string) => number;
  retryScope: (eventId: string) => void;
  deny: (eventId: string, error: unknown) => void;
  generation: (eventId: string) => number;
  completionKey: (scope: EventScope, type: EventOperationType) => string;
  blocked: (scope: EventScope, type: EventOperationType) => boolean;
  run: (
    scope: EventScope,
    type: EventOperationType,
    send: Send,
  ) => Promise<EventFeedback>;
  lookup: (record: EventOperation) => Promise<void>;
  restore: () => void;
}
const Context = createContext<Runtime | null>(null);

export function EventRuntimeProvider({
  config,
  children,
}: {
  config: EventRuntimeConfig;
  children: ReactNode;
}) {
  const recovery = useEventRecovery(config.binding);
  const latest = useRef(config);
  const mounted = useRef(false);
  const controller = useRef<AbortController | null>(null);
  const [pending, setPending] = useState(false);
  const [lookupFailed, setLookupFailed] = useState(false);
  const [denied, setDenied] = useState<Record<string, boolean>>({});
  const denial = useRef<Record<string, boolean>>({});
  const [epochs, setEpochs] = useState<Record<string, number>>({});
  const [changes, setChanges] = useState<Record<string, number>>({});
  const [completions, setCompletions] = useState<Record<string, string>>({});
  useEffect(() => {
    latest.current = config;
  }, [config]);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      controller.current?.abort();
    };
  }, []);
  function deny(eventId: string, error: unknown) {
    denial.current[eventId] = true;
    setDenied((before) => ({ ...before, [eventId]: true }));
    setEpochs((before) => ({
      ...before,
      [eventId]: (before[eventId] ?? 0) + 1,
    }));
    recovery.current.current
      .filter((item) => item.eventId === eventId)
      .forEach((item) => recovery.remove(item.idempotencyKey));
    if (
      error instanceof ApiError &&
      (error.kind === "unauthorized" ||
        error.code === "supplier_context_changed")
    )
      latest.current.invalidate(error);
  }
  function changed(record: EventOperation, recovered: boolean) {
    invalidateEvent(record.eventId);
    if (recovered) recovery.resolve(record, "Completed");
    else recovery.remove(record.idempotencyKey);
    setChanges((before) => ({
      ...before,
      [record.eventId]: (before[record.eventId] ?? 0) + 1,
    }));
    if (recovered)
      setCompletions((before) => ({
        ...before,
        [eventIntent(record, record.operationType)]: record.idempotencyKey,
      }));
  }
  function retryScope(eventId: string) {
    denial.current[eventId] = false;
    setEpochs((before) => ({
      ...before,
      [eventId]: (before[eventId] ?? 0) + 1,
    }));
    setDenied((before) => ({ ...before, [eventId]: false }));
  }
  function blocked(scope: EventScope, type: EventOperationType) {
    return (
      !recovery.available ||
      pending ||
      Boolean(denied[scope.eventId]) ||
      recovery.records.some(
        (item) =>
          eventIntent(item, item.operationType) === eventIntent(scope, type),
      )
    );
  }
  async function perform(
    scope: EventScope,
    type: EventOperationType,
    send: Send,
  ): Promise<EventFeedback> {
    const signal = controller.current!.signal;
    const record: EventOperation = {
      ...scope,
      operationType: type,
      idempotencyKey: createCommandKey("event"),
      startedAt: Date.now(),
    };
    try {
      if (
        recovery
          .read()
          .some(
            (item) =>
              eventIntent(item, item.operationType) ===
              eventIntent(scope, type),
          )
      )
        return "blocked";
      if (!(await latest.current.verify(signal))) return "forbidden";
      if (signal.aborted || !mounted.current) return "unknown";
      if (!recovery.save(record)) return "storage";
      const result = await send({
        tenantId: config.binding.tenantId,
        csrfToken: latest.current.csrfToken,
        idempotencyKey: record.idempotencyKey,
        signal,
      });
      if (!mounted.current || signal.aborted || denial.current[scope.eventId])
        return "unknown";
      if (!eventCompletion(result.data, record)) return "unknown";
      changed(record, false);
      return "success";
    } catch (error) {
      if (!mounted.current || signal.aborted) return "unknown";
      const outcome = eventFeedback(error);
      if (outcome !== "unknown") recovery.remove(record.idempotencyKey);
      if (eventAuthorityLost(error)) deny(scope.eventId, error);
      return outcome;
    }
  }
  async function run(
    scope: EventScope,
    type: EventOperationType,
    send: Send,
  ): Promise<EventFeedback> {
    if (controller.current || blocked(scope, type)) return "blocked";
    if (!navigator.locks) return "storage";
    controller.current = new AbortController();
    setPending(true);
    try {
      return await navigator.locks.request(
        eventBindingKey(config.binding),
        { ifAvailable: true },
        (lock) =>
          lock
            ? perform(scope, type, send)
            : Promise.resolve("blocked" as const),
      );
    } catch {
      return "storage";
    } finally {
      controller.current = null;
      if (mounted.current) setPending(false);
    }
  }
  async function lookup(record: EventOperation) {
    if (controller.current || denial.current[record.eventId]) return;
    controller.current = new AbortController();
    const signal = controller.current.signal;
    setPending(true);
    setLookupFailed(false);
    try {
      if (!(await latest.current.verify(signal))) return;
      const response =
        config.binding.audience === "supplier"
          ? await supplierOperationQuery(
              config.binding,
              { eventId: record.eventId, grantId: record.grantId! },
              record.operationType,
              record.idempotencyKey,
              signal,
            )
          : await getOperationByKey(
              { tenantId: config.binding.tenantId, signal },
              record.operationType,
              record.idempotencyKey,
            );
      if (!mounted.current || signal.aborted || denial.current[record.eventId])
        return;
      if (response.data.state === "Failed") {
        recovery.resolve(record, "Failed");
        return;
      }
      if (
        response.data.state !== "Completed" ||
        !eventCompletion(response.data.result, record)
      ) {
        setLookupFailed(true);
        return;
      }
      changed(record, true);
    } catch (error) {
      if (!mounted.current || signal.aborted) return;
      if (eventAuthorityLost(error)) deny(record.eventId, error);
      else setLookupFailed(true);
    } finally {
      controller.current = null;
      if (mounted.current) setPending(false);
    }
  }
  return (
    <Context.Provider
      value={{
        config,
        binding: config.binding,
        records: recovery.records,
        storageAvailable: recovery.available,
        storageReady: recovery.ready,
        terminals: recovery.terminals,
        dismiss: recovery.dismiss,
        pending,
        lookupFailed,
        isDenied: (id) => Boolean(denied[id]),
        accessEpoch: (id) => epochs[id] ?? 0,
        retryScope,
        deny,
        generation: (id) => changes[id] ?? 0,
        completionKey: (scope, type) =>
          completions[eventIntent(scope, type)] ?? "initial",
        blocked,
        run,
        lookup,
        restore: recovery.sync,
      }}
    >
      {children}
    </Context.Provider>
  );
}

export function useEventRuntime() {
  const value = useContext(Context);
  if (!value) throw new Error("Event runtime is unavailable");
  return value;
}
